/**
 * A point is a 2D array
 */
export type Point = [x: number, y: number];

/**
 * A polygon is made up of a series of loops
 * - The first loop must be CCW and the following loops should be CW.
 * - The loops should be non-overlapping.
 * - The loops can only be singly nested (i.e. one CCW loop followed by CW loops)
 */
export type PolygonLoop = Point[];
export type Polygon = PolygonLoop[];

export type HalfEdge = [startVertex: number, endVertex: number];
export type Segment = [ax: number, ay: number, bx: number, by: number];

/**
 * Doubly connected edge list: A collection of half edges that join vertices to form
 * a planar graph. Faces may have holes which are called child faces.
 */
export class DCEL {
  constructor(
    /**
     * An array of 2d points - each point is a vertex
     **/
    public vertices: Point[],
    /**
     * a series of [vertexId, vertexId] pairs representing the half edge connections
     **/
    public halfEdges: HalfEdge[],
    /**
     * an array mapping from the vertex index to a list of half edges emanating from it
     **/
    public vertexHalfEdges: number[][],
    /**
     * the index of the half edge in the sorted vertexHalfEdges array
     **/
    public halfEdgeVertexIndices: number[],
    /**
     * Each face is described by a half edge sequence. Interior faces form a CCW region and
     * exterior faces are CW.
     **/
    public faceHalfEdges: number[][],
    /**
     * Interior faces are positive and "exterior" faces are negative. Each interior face
     * half edge has an exterior half edge on its opposite side. note, that an interior face
     * is not a loop that is geometrically interior to another race - that is known as a child face.
     **/
    public faceSignedAreas: number[],
    /**
     * Each half edge only has a single parent face.
     * This array maps each half edge to its parent face
     **/
    public halfEdgeToFace: number[],
    /**
     * half edges formed from `dcelFromPolygons` may originate from shared by multiple polygons.
     * ie. when two polygons have collinear, similarly oriented edges
     **/
    public halfEdgeToPolygon: number[][],
    /**
     * identifies the polygons this face is contained within. this is necessary for set ops.
     **/
    public faceToPolygons: Set<number>[],
    /**
     * An interior face may surround an exterior face. Conceptually, this is a "hole" in the
     * interior face. We call the surrounding face a "parent face".
     **/
    public faceToParentFace: number[],
    /**
     * the reverse mapping of faceToParentFace
     **/
    public faceToChildFaces: number[][],
    /**
     * New segment associations to support half edge <=> room edge relations.
     **/
    public segmentToPolygon: number[],
    /**
     * The polygon loop index that the segment belongs to.
     **/
    public segmentToPolygonLoop: number[],
    /**
     * The edge index that the segment belongs to.
     **/
    public segmentToPolygonLoopEdge: number[],
    /**
     * The half edges that the segment belongs to.
     **/
    public segmentToHalfEdges: number[][],
  ) {}

  private _halfEdgeToSegment?: number[];

  getHalfEdgeToSegment(): number[] {
    if (!this._halfEdgeToSegment) {
      const halfEdgeToSegment = new Array<number>(this.halfEdges.length).fill(-1);
      for (let segIdx = 0; segIdx < this.segmentToHalfEdges.length; segIdx++) {
        const hes = this.segmentToHalfEdges[segIdx];
        if (hes) {
          for (const he of hes) {
            halfEdgeToSegment[he] = segIdx;
          }
        }
      }
      this._halfEdgeToSegment = halfEdgeToSegment;
    }
    return this._halfEdgeToSegment;
  }

  buildPolygonLoopFromHalfEdges(halfEdges: number[]): PolygonLoop {
    const pts: Point[] = [];

    for (const halfEdge of halfEdges) {
      pts.push(this.vertices[this.halfEdges[halfEdge][0]]);
    }

    return pts;
  }

  buildPolygonFromFace(faceId: number): Polygon {
    const polygon: Polygon = [];

    const parentFace = this.faceHalfEdges[faceId];
    if (!parentFace) {
      return [];
    }

    const outerloop = this.buildPolygonLoopFromHalfEdges(parentFace);
    polygon.push(outerloop);

    for (const childFaceId of this.faceToChildFaces[faceId]) {
      const loop = this.buildPolygonLoopFromHalfEdges(this.faceHalfEdges[childFaceId]);
      polygon.push(loop);
    }

    return polygon;
  }

  faceIsInterior(faceId: number) {
    return this.faceSignedAreas[faceId] > 0;
  }

  buildInteriorHalfEdgePairs(): [number, number][] {
    const interiorEdgePairs: [number, number][] = [];
    const edgeMap = new Map<string, number>();

    for (let i = 0; i < this.halfEdges.length; i++) {
      const [start, end] = this.halfEdges[i];
      const edgeKey = `${start},${end}`;
      const oppositeKey = `${end},${start}`;

      if (edgeMap.has(oppositeKey)) {
        const oppositeIndex = edgeMap.get(oppositeKey);
        if (oppositeIndex === undefined) {
          continue;
        }
        const faceA = this.halfEdgeToFace[i];
        const faceB = this.halfEdgeToFace[oppositeIndex];

        if (this.faceIsInterior(faceA) && this.faceIsInterior(faceB)) {
          interiorEdgePairs.push([i, oppositeIndex]);
        }
      } else {
        edgeMap.set(edgeKey, i);
      }
    }
    return interiorEdgePairs;
  }
}
